import { useCallback, useEffect, useRef, useState } from "react"
import { loadTurnstileScript, waitForTurnstile } from "../utils/turnstile"

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
const TURNSTILE_SITE_KEY = process.env.GATSBY_TURNSTILE_SITE_KEY

export const useTurnstile = ({
  containerPrefix = "turnstile-widget",
  siteKey = TURNSTILE_SITE_KEY,
  theme = "light",
  onVerify = () => {},
  onError = () => {},
  onExpired = () => {},
} = {}) => {
  const [turnstileToken, setTurnstileToken] = useState("")
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null)
  const [turnstileInstanceKey, setTurnstileInstanceKey] = useState(0)
  const onVerifyRef = useRef(onVerify)
  const onErrorRef = useRef(onError)
  const onExpiredRef = useRef(onExpired)
  const turnstileContainerId = `${containerPrefix}-${turnstileInstanceKey}`

  useEffect(() => {
    onVerifyRef.current = onVerify
    onErrorRef.current = onError
    onExpiredRef.current = onExpired
  }, [onVerify, onError, onExpired])

  useEffect(() => {
    let cancelled = false

    const init = async () => {
      try {
        if (!siteKey) {
          throw new Error("Missing GATSBY_TURNSTILE_SITE_KEY")
        }

        await loadTurnstileScript({ scriptSrc: TURNSTILE_SCRIPT_SRC })
        if (cancelled) {
          return
        }
        await waitForTurnstile()

        const container = document.getElementById(turnstileContainerId)
        if (!container) {
          return
        }

        const render = () => {
          if (cancelled) {
            return
          }

          container.innerHTML = ""
          const widgetId = window.turnstile.render(container, {
            sitekey: siteKey,
            theme,
            callback: (token) => {
              const nextToken = token ?? ""
              setTurnstileToken(nextToken)
              onVerifyRef.current(nextToken)
            },
            "error-callback": () => {
              setTurnstileToken("")
              onErrorRef.current("Verification failed. Please try again.")
            },
            "expired-callback": () => {
              setTurnstileToken("")
              onExpiredRef.current("Verification expired. Please try again.")
            },
          })

          setTurnstileWidgetId(widgetId)
        }

        render()
      } catch (e) {
        if (cancelled) {
          return
        }
        console.error(e)
        setTurnstileToken("")
        onErrorRef.current("Turnstile failed to load. Please try again later.")
      }
    }

    init()

    return () => {
      cancelled = true
      setTurnstileWidgetId(null)
    }
  }, [containerPrefix, siteKey, theme, turnstileContainerId])

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("")
    if (
      typeof window !== "undefined" &&
      window.turnstile &&
      turnstileWidgetId != null
    ) {
      try {
        window.turnstile.reset(turnstileWidgetId)
      } catch (e) {
        setTurnstileInstanceKey((key) => key + 1)
      }
      return
    }

    setTurnstileInstanceKey((key) => key + 1)
  }, [turnstileWidgetId])

  return {
    turnstileContainerId,
    turnstileToken,
    resetTurnstile,
  }
}
